import { CHART_TABS, ZONE_COLORS, formatPace } from "./utils";

function StatCard({ label, value, unit, isActive, color, onClick, disabled }) {
  return (
    <div
      onClick={!disabled ? onClick : undefined}
      style={{
        background: isActive ? color : "var(--bg-card)",
        borderRadius: 14,
        padding: "14px 10px",
        textAlign: "center",
        cursor: disabled ? "default" : "pointer",
        transition: "all 0.15s",
      }}
    >
      <div
        style={{
          fontSize: 20,
          fontWeight: "bold",
          color: isActive ? "#fff" : "var(--text-primary)",
          lineHeight: 1,
        }}
      >
        {value}
        {unit && <span style={{ fontSize: 12, marginLeft: 3, fontWeight: "normal" }}>{unit}</span>}
      </div>
      <div
        style={{ fontSize: 11, color: isActive ? "rgba(255,255,255,0.75)" : "#888", marginTop: 4 }}
      >
        {label}
      </div>
    </div>
  );
}

export function HighlightStats({
  run,
  chartMode,
  hoverIndex,
  hoverValue,
  availableTabs,
  velocityData,
  hrData,
  onChartModeChange,
}) {
  const paceFormatted = formatPace(run.distance / run.moving_time);

  // Live values while hovering
  function getLiveValue(key) {
    if (hoverIndex === null || hoverValue === null) return null;
    if (key === chartMode) return hoverValue;
    return null;
  }

  function formatLive(key, val) {
    if (val === null) return null;
    if (key === "hr") return `${Math.round(val)}`;
    if (key === "pace") {
      const mins = Math.floor(val);
      const secs = Math.round((val - mins) * 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    }
    if (key === "elevation") return `${Math.round(val)}`;
    return null;
  }

  const stats = [
    {
      key: "pace",
      label: "Avg. Pace",
      value: formatLive("pace", getLiveValue("pace")) ?? paceFormatted,
      unit: null,
    },
    {
      key: "elevation",
      label: "Elevation",
      value:
        formatLive("elevation", getLiveValue("elevation")) ??
        `${Math.round(run.total_elevation_gain)}`,
      unit: "m",
    },
    {
      key: "hr",
      label: "Avg HR",
      value:
        formatLive("hr", getLiveValue("hr")) ??
        (run.average_heartrate ? `${Math.round(run.average_heartrate)}` : "—"),
      unit: run.average_heartrate ? "BPM" : "",
    },
  ];

  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}
    >
      {stats.map(s => {
        const tabColor = CHART_TABS.find(t => t.key === s.key)?.color || "#1A1A2E";
        const available = availableTabs.some(t => t.key === s.key);
        return (
          <StatCard
            key={s.key}
            label={s.label}
            value={s.value}
            unit={s.unit}
            isActive={chartMode === s.key}
            color={tabColor}
            onClick={() => onChartModeChange(s.key)}
            disabled={!available}
          />
        );
      })}
    </div>
  );
}

export function ZoneDistribution({ zoneDist, totalHrPoints, onZoneHover, onZoneHoverEnd }) {
  if (!totalHrPoints) return null;
  return (
    <div style={{ background: "var(--bg-card)", borderRadius: 14, padding: "14px 16px", marginBottom: 12 }}>
      <div
        style={{
          fontSize: 11,
          color: "var(--text-subtle)",
          letterSpacing: 1,
          marginBottom: 10,
          fontFamily: "monospace",
        }}
      >
        HR ZONE DISTRIBUTION
      </div>
      <div
        style={{
          display: "flex",
          height: 8,
          borderRadius: 99,
          overflow: "hidden",
          marginBottom: 10,
        }}
      >
        {[1, 2, 3, 4, 5].map(z => {
          const pct = Math.round((zoneDist[z] / totalHrPoints) * 100);
          return pct > 0 ? (
            <div
              key={z}
              style={{ width: `${pct}%`, background: ZONE_COLORS[z], cursor: "pointer" }}
              onMouseEnter={() => onZoneHover?.(z)}
              onMouseLeave={() => onZoneHoverEnd?.()}
            />
          ) : null;
        })}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {[1, 2, 3, 4, 5].map(z => {
          const pct = Math.round((zoneDist[z] / totalHrPoints) * 100);
          if (!pct) return null;
          return (
            <div
              key={z}
              style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}
              onMouseEnter={() => onZoneHover?.(z)}
              onMouseLeave={() => onZoneHoverEnd?.()}
            >
              <div
                style={{ width: 8, height: 8, borderRadius: "50%", background: ZONE_COLORS[z] }}
              />
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                Z{z}: {pct}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ExtraStats({ run }) {
  const items = [
    {
      label: "Avg HR",
      value: run.average_heartrate ? `${Math.round(run.average_heartrate)} bpm` : "—",
    },
    { label: "Max HR", value: run.max_heartrate ? `${Math.round(run.max_heartrate)} bpm` : "—" },
    {
      label: "Cadence",
      value: run.average_cadence ? `${Math.round(run.average_cadence * 2)} spm` : "—",
    },
    { label: "Calories", value: run.calories ? `${Math.round(run.calories)} kcal` : "—" },
    {
      label: "Avg Speed",
      value: run.average_speed ? `${(run.average_speed * 3.6).toFixed(1)} km/h` : "—",
    },
    { label: "Elevation", value: `${Math.round(run.total_elevation_gain)} m` },
  ];
  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}
    >
      {items.map((s, i) => (
        <div
          key={i}
          style={{
            background: "var(--bg-card)",
            borderRadius: 14,
            padding: "12px 10px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: "bold", color: "var(--text-primary)" }}>{s.value}</div>
          <div style={{ fontSize: 10, color: "var(--text-subtle)", marginTop: 3 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}
